import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { BACKEND_URL } from "../../api/api";

/**
 * Componente para validar fichajes pendientes.
 * Muestra fichajes con diferencia horaria sospechosa que requieren aprobación manual del admin.
 * 
 * @returns {JSX.Element} Sección de validación de fichajes
 */
export default function ValidacionFichajesSection() {
  const { token } = useContext(AuthContext);
  const [fichajesPendientes, setFichajesPendientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validandoId, setValidandoId] = useState(null);
  const [modalValidacion, setModalValidacion] = useState(null); // {fichaje, aprobar}
  const [notas, setNotas] = useState("");

  useEffect(() => {
    cargarFichajesPendientes();
  }, []);

  const cargarFichajesPendientes = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/v1/fichajes/pendientes-validacion`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      setFichajesPendientes(data);
    } catch (error) {
      console.error("Error al cargar fichajes pendientes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidar = async () => {
    if (!modalValidacion) return;

    try {
      setValidandoId(modalValidacion.fichaje._id);
      const res = await fetch(`${BACKEND_URL}/api/v1/fichajes/${modalValidacion.fichaje._id}/validar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          aprobar: modalValidacion.aprobar,
          notas: notas || undefined,
        }),
      });

      if (res.ok) {
        // Recargar lista
        await cargarFichajesPendientes();
        setModalValidacion(null);
        setNotas("");
      } else {
        const error = await res.json();
        alert(error.message || "Error al validar fichaje");
      }
    } catch (error) {
      console.error("Error al validar fichaje:", error);
      alert("Error al validar fichaje");
    } finally {
      setValidandoId(null);
    }
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatearDiferencia = (minutos) => {
    if (minutos < 60) {
      return `${Math.round(minutos)} minutos`;
    }
    const horas = Math.floor(minutos / 60);
    const mins = Math.round(minutos % 60);
    return `${horas}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Validación de Fichajes
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Fichajes pendientes que requieren aprobación manual
          </p>
        </div>
        <button
          onClick={cargarFichajesPendientes}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition disabled:opacity-50"
        >
          <i className="fas fa-sync-alt"></i>
          Actualizar
        </button>
      </div>

      {fichajesPendientes.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-check-circle text-green-600 text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            ¡Todo al día!
          </h3>
          <p className="text-gray-600">
            No hay fichajes pendientes de validación
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {fichajesPendientes.map((fichaje) => (
            <div
              key={fichaje._id}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-4 md:p-6 border-l-4 border-yellow-500"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Info del usuario y fichaje */}
                <div className="flex items-start gap-4">
                  <img
                    src={
                      fichaje.userId?.imagenPerfil
                        ? `${BACKEND_URL}${fichaje.userId.imagenPerfil}`
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            fichaje.userId?.nombre + " " + fichaje.userId?.apellidos
                          )}&background=4F46E5&color=fff&size=128`
                    }
                    alt={fichaje.userId?.nombre}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-800">
                      {fichaje.userId?.nombre} {fichaje.userId?.apellidos}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {fichaje.userId?.email}
                    </p>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <i className="fas fa-clock text-gray-500"></i>
                        <span className="font-medium">
                          {fichaje.tipo === "entrada" ? "Entrada" : "Salida"}:
                        </span>
                        <span>{formatearFecha(fichaje.fecha)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <i className="fas fa-exclamation-triangle text-yellow-600"></i>
                        <span className="font-medium">Diferencia horaria:</span>
                        <span className="text-yellow-700 font-semibold">
                          {formatearDiferencia(fichaje.timeDifferenceMinutes)}
                        </span>
                      </div>
                      {fichaje.notas && (
                        <div className="flex items-start gap-2 text-sm mt-2">
                          <i className="fas fa-sticky-note text-gray-500 mt-0.5"></i>
                          <div>
                            <span className="font-medium">Notas:</span>
                            <p className="text-gray-600 mt-1">{fichaje.notas}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Botones de acción */}
                <div className="flex flex-col sm:flex-row gap-2 md:min-w-[200px]">
                  <button
                    onClick={() => setModalValidacion({ fichaje, aprobar: true })}
                    disabled={validandoId === fichaje._id}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 font-medium"
                  >
                    <i className="fas fa-check"></i>
                    Aprobar
                  </button>
                  <button
                    onClick={() => setModalValidacion({ fichaje, aprobar: false })}
                    disabled={validandoId === fichaje._id}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 font-medium"
                  >
                    <i className="fas fa-times"></i>
                    Rechazar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de confirmación */}
      {modalValidacion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    modalValidacion.aprobar
                      ? "bg-green-100"
                      : "bg-red-100"
                  }`}
                >
                  <i
                    className={`fas ${
                      modalValidacion.aprobar
                        ? "fa-check text-green-600"
                        : "fa-times text-red-600"
                    } text-xl`}
                  ></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  {modalValidacion.aprobar ? "Aprobar" : "Rechazar"} fichaje
                </h3>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Usuario:</strong> {modalValidacion.fichaje.userId?.nombre}{" "}
                  {modalValidacion.fichaje.userId?.apellidos}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Tipo:</strong>{" "}
                  {modalValidacion.fichaje.tipo === "entrada" ? "Entrada" : "Salida"}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Fecha:</strong> {formatearFecha(modalValidacion.fichaje.fecha)}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Diferencia:</strong>{" "}
                  {formatearDiferencia(modalValidacion.fichaje.timeDifferenceMinutes)}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notas {!modalValidacion.aprobar && "(obligatorio)"}
                </label>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  placeholder={
                    modalValidacion.aprobar
                      ? "Añade un comentario sobre esta aprobación (opcional)"
                      : "Explica por qué rechazas este fichaje"
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setModalValidacion(null);
                    setNotas("");
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleValidar}
                  disabled={!modalValidacion.aprobar && !notas.trim()}
                  className={`flex-1 px-5 py-3 rounded-lg transition font-medium flex items-center justify-center gap-2 ${
                    modalValidacion.aprobar
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-red-600 text-white hover:bg-red-700"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <i
                    className={`fas ${
                      modalValidacion.aprobar ? "fa-check" : "fa-times"
                    }`}
                  ></i>
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
